'use client';

import { cn } from '@/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import {
  createContext,
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './accordion.module.scss';

type AccordionContextType = {
  allowMultiple: boolean;
  openItems: string[];
  toggleItem: (id: string) => void;
};

type AccordionProps = {
  children: ReactNode;
  allowMultiple?: boolean;
  defaultOpen?: string[];
};

type AccordionItemProps = {
  children: ReactNode;
  id: string;
  title: string;
};

const AccordionContext = createContext<AccordionContextType | null>(null);

const useAccordion = () => {
  const context = useContext(AccordionContext);
  if (!context) throw new Error('useAccordion must be used within an Accordion');
  return context;
};

export const Accordion = ({
  children,
  allowMultiple = false,
  defaultOpen = [],
  className,
  ...props
}: AccordionProps & HTMLAttributes<HTMLDivElement>) => {
  const [openItems, setOpenItems] = useState<string[]>(defaultOpen);

  const toggleItem = (id: string) => {
    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
      );
    } else {
      setOpenItems((prev) => (prev.includes(id) ? [] : [id]));
    }
  };

  return (
    <AccordionContext.Provider value={{ allowMultiple, openItems, toggleItem }}>
      <div role="presentation" className={cn(styles.component, className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

export const AccordionItem = ({
  id,
  title,
  children,
  className,
  ...props
}: AccordionItemProps & HTMLAttributes<HTMLDivElement>) => {
  const { openItems, toggleItem } = useAccordion();
  const isOpen = openItems.includes(id);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });

      resizeObserver.observe(contentRef.current);
      setContentHeight(contentRef.current.scrollHeight);

      return () => resizeObserver.disconnect();
    }
  }, [children]);

  return (
    <div className={cn(styles.component_item, className)} {...props}>
      <button
        id={`accordion-trigger-${id}`}
        onClick={() => toggleItem(id)}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${id}`}
        className={styles.component_trigger}
      >
        <span className={styles.component_title}>{title}</span>
        <div className={styles.component_icon_container}>
          <span
            className={cn(styles.component_icon_wrapper, {
              [styles.component_icon_wrapper_open]: isOpen,
            })}
            aria-hidden="true"
          >
            <ChevronDownIcon className={styles.component_icon} />
          </span>
        </div>
      </button>
      <div
        id={`accordion-content-${id}`}
        className={cn(styles.component_content, {
          [styles.component_content_open]: isOpen,
        })}
        role="region"
        aria-labelledby={`accordion-trigger-${id}`}
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0px',
        }}
      >
        <div
          ref={contentRef}
          className={cn(styles.component_content_inner, {
            [styles.component_content_inner_open]: isOpen,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
